import {
  BellRing,
  type LucideIcon,
  ShieldCheck,
  UploadCloud,
  User,
  UserPlus,
} from "lucide-react";

const ACCOUNT_TABS = [
  "profile",
  "security",
  "notifications",
  "cvs",
  "verification",
] as const;

export type AccountTab = (typeof ACCOUNT_TABS)[number];

type AccountNavItem = {
  tab: AccountTab;
  label: string;
  tabLabel: string;
  title: string;
  description: string;
  icon: LucideIcon;
};

const ACCOUNT_NAV: AccountNavItem[] = [
  {
    tab: "profile",
    label: "Profile",
    tabLabel: "Profile",
    title: "Profile",
    description: "Your name, photo, and contact details",
    icon: User,
  },
  {
    tab: "security",
    label: "Sign-in & security",
    tabLabel: "Security",
    title: "Sign-in & security",
    description: "Sign-in methods, account type, and deletion",
    icon: ShieldCheck,
  },
  {
    tab: "notifications",
    label: "Notifications",
    tabLabel: "Notifications",
    title: "Notifications",
    description: "Choose how you receive updates",
    icon: BellRing,
  },
  {
    tab: "cvs",
    label: "CVs & documents",
    tabLabel: "CVs",
    title: "CVs & documents",
    description: "Upload and manage your CV files",
    icon: UploadCloud,
  },
  {
    tab: "verification",
    label: "Identity verification",
    tabLabel: "Verification",
    title: "Identity verification",
    description: "Verify your phone number and ID",
    icon: UserPlus,
  },
];

function isAccountTab(tab: string): tab is AccountTab {
  return (ACCOUNT_TABS as readonly string[]).includes(tab);
}

function isTabAllowedForUser(
  tab: AccountTab,
  userType: "employee" | "employer" | undefined
): boolean {
  if (userType === "employer") {
    return tab !== "cvs" && tab !== "verification";
  }

  if (userType === "employee") {
    return true;
  }

  return tab !== "cvs" && tab !== "verification";
}

function defaultAccountTab(
  _userType: "employee" | "employer" | undefined
): AccountTab {
  return "profile";
}

export function getAccountNavItems(
  userType: "employee" | "employer" | undefined
): AccountNavItem[] {
  return ACCOUNT_NAV.filter((item) => isTabAllowedForUser(item.tab, userType));
}

export function getAccountNavItem(tab: string): AccountNavItem | undefined {
  return ACCOUNT_NAV.find((item) => item.tab === tab);
}

export function accountPath({
  tab,
  query,
}: {
  tab: AccountTab;
  query?: Record<string, string>;
}): string {
  const params = new URLSearchParams({ tab });
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      params.set(key, value);
    }
  }
  return `/account?${params.toString()}`;
}

export function accountPathForPersonalDefault(): string {
  return accountPath({ tab: "profile" });
}

export function resolveAccountLocation({
  tabParam,
  userType,
}: {
  tabParam: string | null;
  userType: "employee" | "employer" | undefined;
}): { tab: AccountTab } {
  const defaultTab = defaultAccountTab(userType);

  if (!(tabParam && isAccountTab(tabParam))) {
    return { tab: defaultTab };
  }

  if (!isTabAllowedForUser(tabParam, userType)) {
    return { tab: defaultTab };
  }

  return { tab: tabParam };
}

export function canonicalAccountSearchParams({
  tabParam,
  userType,
  searchParams,
}: {
  tabParam: string | null;
  userType: "employee" | "employer" | undefined;
  searchParams: URLSearchParams;
}): URLSearchParams {
  const { tab } = resolveAccountLocation({ tabParam, userType });
  const params = new URLSearchParams();

  params.set("tab", tab);

  for (const [key, value] of searchParams.entries()) {
    if (key === "tab") {
      continue;
    }
    params.set(key, value);
  }

  return params;
}
